import { cn } from "@/lib/utils";
import { Column, Table } from "@tanstack/react-table";
import { useState } from "react";
import { formatISO, isValid, parseISO } from "date-fns";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FormInput } from "@/components/FormComponents/FormInput";
import { FormSelect } from "@/components/FormComponents/FormSelect";
import { Button } from "@/components/ui/button";
import { Filter as FilterIcon } from "lucide-react";
import { FormCalendar } from "@/components/FormComponents/FormCalendar";
import { motion, AnimatePresence } from "motion/react";

const parseDateString = (dateString: unknown): Date | undefined => {
  if (!dateString) return undefined;
  try {
    if (typeof dateString === "string") {
      const parsedDate = parseISO(dateString);
      return isValid(parsedDate) ? parsedDate : undefined;
    }
    return undefined;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return undefined;
  }
};

export const Filter = ({
  column,
  table,
}: {
  column: Column<any, any>;
  table: Table<any>;
}) => {
  const [open, setOpen] = useState(false);
  const uniqueValues = Array.from(
    new Set(
      table
        .getPreFilteredRowModel()
        .flatRows.map((row) => row.getValue(column.id))
    )
  );

  const columnFilterValue = column.getFilterValue();
  const [filteredValue, setFilteredValue] = useState<any>(columnFilterValue);
  const filterType = column.columnDef.meta?.filterType;

  const renderFilterInput = () => {
    if (filterType === "date") {
      return (
        <motion.div
          key="date-filter"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          <FormCalendar
            className="w-full"
            value={
              filteredValue !== undefined
                ? parseISO(filteredValue as string)
                : columnFilterValue !== undefined
                ? parseISO(columnFilterValue as string)
                : undefined
            }
            onSelect={(e) => {
              setFilteredValue(formatISO(e).split("T")[0]);
            }}
            disabled={(date) => {
              const availableDates = table
                .getPreFilteredRowModel()
                .flatRows.map((row) => {
                  return parseDateString(row.getValue(column.id));
                })
                .filter((d): d is Date => d !== undefined)
                .map((d) => formatISO(d).split("T")[0]);

              return !availableDates.includes(formatISO(date).split("T")[0]);
            }}
          />
        </motion.div>
      );
    }

    if (filterType === "number") {
      return (
        <motion.div
          key="number-filter"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="flex gap-3"
        >
          <FormInput
            type="number"
            value={(columnFilterValue as [number, number])?.[0] ?? ""}
            onChange={(e) =>
              setFilteredValue((old: [number, number]) => [
                e.target.value,
                old?.[1] ?? (columnFilterValue as [number, number])?.[1],
              ])
            }
            placeholder="Min"
            className="w-full rounded-lg border border-gray-200 dark:border-gray-800"
            numeric
          />
          <FormInput
            type="number"
            value={(columnFilterValue as [number, number])?.[1] ?? ""}
            onChange={(e) =>
              setFilteredValue((old: [number, number]) => [
                old?.[0] ?? (columnFilterValue as [number, number])?.[0],
                e.target.value,
              ])
            }
            placeholder="Maks"
            className="w-full rounded-lg border border-gray-200 dark:border-gray-800"
            numeric
          />
        </motion.div>
      );
    }

    if (filterType === "select") {
      return (
        <FormSelect
          options={uniqueValues.map((value) => ({
            label: value as string,
            value: value as string,
          }))}
          onChange={(evt) => setFilteredValue(evt.target.value)}
          onClick={(evt) => evt.stopPropagation()}
          placeholder="Seçiniz"
          value={
            (filteredValue !== undefined
              ? filteredValue
              : columnFilterValue ?? "") as string
          }
        />
      );
    }

    if (filterType === "boolean") {
      return (
        <motion.div
          key="boolean-filter"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          <FormSelect
            options={[
              { label: "Evet", value: "true" },
              { label: "Hayır", value: "false" },
            ]}
            placeholder="Seçiniz"
            onChange={(evt) => {
              setFilteredValue(evt.target.value);
            }}
            value={
              (filteredValue !== undefined
                ? filteredValue
                : columnFilterValue?.toString() ?? "") as string
            }
            className="rounded-lg border border-gray-200 dark:border-gray-800"
          />
        </motion.div>
      );
    }

    // Default text input
    return (
      <motion.div
        key="string-filter"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
      >
        <FormInput
          className="w-full rounded-lg border border-gray-200 dark:border-gray-800"
          onChange={(evt) => setFilteredValue(evt.target.value)}
          onClick={(evt) => evt.stopPropagation()}
          placeholder="Ara"
          type="text"
          value={filteredValue ?? columnFilterValue ?? ""}
        />
      </motion.div>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "p-1.5 rounded-full transition-colors hover:bg-green-100 group/filter",
          {
            "bg-green-100": columnFilterValue !== undefined,
          }
        )}
      >
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
          <FilterIcon
            className={cn("size-4 group-hover/filter:text-green-600", {
              "text-green-600": columnFilterValue !== undefined,
              "text-gray-800": columnFilterValue === undefined,
            })}
          />
        </motion.div>
      </PopoverTrigger>
      <PopoverContent
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col gap-3 p-4 shadow-lg border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900"
      >
        <AnimatePresence>
          <>
            {renderFilterInput()}
            <div className="flex gap-2 mt-2">
              <Button
                variant="outline"
                className="flex-1 flex justify-center items-center hover:bg-gray-100 dark:hover:bg-gray-800"
                disabled={
                  columnFilterValue === undefined && filteredValue === undefined
                }
                onClick={() => {
                  column.setFilterValue("");
                  setFilteredValue(undefined);
                  setOpen(false);
                }}
              >
                Temizle
              </Button>
              <Button
                className="flex-1 flex justify-center items-center bg-green-600 hover:bg-green-700 text-white"
                disabled={!filteredValue}
                onClick={() => {
                  if (filteredValue === "true" || filteredValue === "false") {
                    column.setFilterValue(filteredValue === "true");
                  } else {
                    column.setFilterValue(filteredValue);
                  }
                  setFilteredValue(undefined);
                  setOpen(false);
                }}
              >
                Uygula
              </Button>
            </div>
          </>
        </AnimatePresence>
      </PopoverContent>
    </Popover>
  );
};
